import { type ReactNode } from "react";

import classNames from "classnames";

import { Spinner } from "../Spinner/Spinner";

export type PricingCardFeatureItemIcon = "check" | "dot";

export interface PricingCardFeatureItem {
  text: string;
  icon?: PricingCardFeatureItemIcon;
  subItems?: string[];
}

export const PRICING_SECTION_KEYS = [
  "projects",
  "storage",
  "import",
  "export",
  "generativeTools",
  "collaborationDocumentation",
  "supportTraining",
  "securityCompliance",
] as const;

export type PricingSectionKey = (typeof PRICING_SECTION_KEYS)[number];

export interface PricingCardFeatureSection {
  category: string;
  /** Used by PricingPage to place this section in the correct grid row. */
  sectionKey?: PricingSectionKey;
  items: PricingCardFeatureItem[];
}

export type PricingCardSeatTypeHighlighted = {
  kind: "highlighted";
  beforeMark: string;
  markText: string;
  afterMark: string;
  markVariant: "blue" | "yellow";
};

export type PricingCardSeatTypePlain = {
  kind: "plain";
  text: string;
};

export type PricingCardSeatType = PricingCardSeatTypeHighlighted | PricingCardSeatTypePlain;

import style from "./PricingPage.module.css"; // eslint-disable-line postcss-modules/no-unused-class

export interface PricingPageCardConfig {
  planName: string;
  planBadgeVariant?: "default" | "dark" | "blue";
  pricePrefix?: string;
  price?: ReactNode;
  pricePeriod?: string;
  priceSubline?: string;
  subtitle?: string;
  tagline?: string;
  features: PricingCardFeatureSection[];
  seatTypes?: PricingCardSeatType[];
  seatTypesLabel?: string;
  highlighted?: boolean;
  ctaSlot: ReactNode;
  children?: ReactNode;
}

export interface PricingPageProps {
  free: PricingPageCardConfig;
  individual: PricingPageCardConfig;
  studio: PricingPageCardConfig;
  enterprise: PricingPageCardConfig;
  className?: string;
}

const CheckIcon = () => (
  <svg
    aria-hidden="true"
    className={style.featureIcon}
    fill="none"
    height="16"
    viewBox="0 0 16 16"
    width="16"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
    <path
      d="M5 8l2 2 4-4"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
  </svg>
);

const DotIcon = () => (
  <svg
    aria-hidden="true"
    className={classNames(style.featureIcon, style.featureIconDot)}
    fill="none"
    height="16"
    viewBox="0 0 16 16"
    width="16"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="8" cy="8" fill="currentColor" r="7" />
    <path
      d="M5 8l2 2 4-4"
      stroke="#fff"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
  </svg>
);

function stripSeatI18nPlaceholder(s: string): string {
  return s.replace(/\u200b/g, "");
}

function buildFeatureMap(
  features: PricingCardFeatureSection[]
): Map<PricingSectionKey, PricingCardFeatureSection> {
  const map = new Map<PricingSectionKey, PricingCardFeatureSection>();
  for (const section of features) {
    if (section.sectionKey) {
      map.set(section.sectionKey, section);
    }
  }
  return map;
}

interface CardProps {
  config: PricingPageCardConfig;
  cardClass: string;
}

function Card({ config, cardClass }: CardProps) {
  const featureMap = buildFeatureMap(config.features);

  return (
    <div className={classNames(style.card, config.highlighted && style.cardHighlighted, cardClass)}>
      {/* Row 1: Badge */}
      <div className={style.badgeRow}>
        <span className={classNames(style.planBadge, style[config.planBadgeVariant ?? "default"])}>
          {config.planName}
        </span>
      </div>

      {/* Row 2: Pricing */}
      <div className={style.pricingRow}>
        <div className={style.priceInner}>
          {config.pricePrefix && <span className={style.pricePrefix}>{config.pricePrefix}</span>}
          <span className={style.price}>{config.price ?? <Spinner size="sm" />}</span>
          <span className={style.pricePeriod}>{config.pricePeriod ?? "/month"}</span>
        </div>
        {config.priceSubline && <p className={style.priceSubline}>{config.priceSubline}</p>}
        {config.subtitle && config.price !== undefined && (
          <p className={style.subtitle}>{config.subtitle}</p>
        )}
      </div>

      {/* Row 3: CTA */}
      <div className={style.ctaRow}>{config.ctaSlot}</div>

      {/* Row 4: Tagline */}
      <div className={style.taglineRow}>
        {config.tagline && <p className={style.tagline}>{config.tagline}</p>}
      </div>

      {/* Rows 5–12: Feature sections — one slot per section key */}
      {PRICING_SECTION_KEYS.map((key) => {
        const section = featureMap.get(key);
        // children slot replaces the securityCompliance row when that section is absent
        const slotContent = key === "securityCompliance" && !section ? config.children : null;
        return (
          <div
            className={classNames(
              style.featureSection,
              !section && !slotContent && style.featureSectionEmpty
            )}
            key={key}
          >
            {slotContent}
            {section && (
              <>
                <span className={style.categoryLabel}>{section.category}</span>
                <ul className={style.items}>
                  {section.items.map((item) => (
                    <li className={style.item} key={item.text}>
                      {item.icon === "dot" ? <DotIcon /> : <CheckIcon />}
                      <div className={style.itemContent}>
                        <span>{item.text}</span>
                        {item.subItems && item.subItems.length > 0 && (
                          <ul className={style.subItems}>
                            {item.subItems.map((sub) => (
                              <li className={style.subItem} key={sub}>
                                {sub}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        );
      })}

      {/* Row 13: Seat types */}
      <div className={style.seatTypesRow}>
        {config.seatTypes && config.seatTypes.length > 0 && (
          <>
            <span className={style.categoryLabel}>
              {config.seatTypesLabel ?? "Available seat types"}
            </span>
            <div className={style.seatTypeLines}>
              {config.seatTypes.map((seat, i) => (
                <p className={style.seatSentence} key={i}>
                  {seat.kind === "plain" ? (
                    seat.text
                  ) : (
                    <span
                      className={classNames(
                        style.seatMarkWrap,
                        seat.markVariant === "blue" ? style.seatMarkBlue : style.seatMarkYellow
                      )}
                    >
                      {stripSeatI18nPlaceholder(seat.beforeMark)}
                      <mark>{seat.markText}</mark>
                      {stripSeatI18nPlaceholder(seat.afterMark)}
                    </span>
                  )}
                </p>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export function PricingPage({ free, individual, studio, enterprise, className }: PricingPageProps) {
  return (
    <div className={classNames(style.wrapper, className)}>
      <div className={style.grid}>
        <Card config={free} cardClass={style.cardFree} />
        <Card config={individual} cardClass={style.cardIndividual} />
        <Card config={studio} cardClass={style.cardStudio} />
        <Card config={enterprise} cardClass={style.cardEnterprise} />
      </div>
    </div>
  );
}
